'use client';

import { motion } from 'framer-motion';

/**
 * Escena de lingotes y moneda con marca Oro Express.
 *
 * Tres piezas con diferente profundidad para dar realismo:
 *  1. Lingote grande de 100 g (frontal, dominante).
 *  2. Moneda 1 oz Oro Express (a la derecha, ligeramente detrás).
 *  3. Lingote pequeño de 50 g (a la izquierda, detrás).
 *
 * No referenciamos refinerías terceras: la marca visible es siempre
 * Oro Express (distribuidor oficial). El asset de logo vive en
 * `public/brand/oro-express-logo.svg`.
 */
export function BullionShowcase() {
  return (
    <div
      className="relative mx-auto aspect-square w-full max-w-lg"
      style={{ perspective: 1400 }}
    >
      <div className="absolute inset-0 -z-10 rounded-full bg-gradient-radial-gold opacity-80 blur-3xl" />

      <BullionDefs />

      {/* Lingote pequeño detrás-izquierda */}
      <motion.div
        initial={{ opacity: 0, y: 24, rotate: -22 }}
        animate={{
          opacity: 1,
          y: [0, -4, 0],
          rotate: -22,
        }}
        transition={{
          opacity: { duration: 0.7, delay: 0.2 },
          y: { duration: 9, repeat: Infinity, ease: 'easeInOut' },
        }}
        className="absolute left-[2%] top-[12%] w-[42%]"
        style={{ transformOrigin: 'center center' }}
      >
        <OroExpressIngot
          weight="50 g"
          serial="N.º OE-50-0234"
          tone="dim"
        />
      </motion.div>

      {/* Moneda detrás-derecha */}
      <motion.div
        initial={{ opacity: 0, scale: 0.7, rotate: 18 }}
        animate={{
          opacity: 1,
          scale: 1,
          rotate: [16, 24, 16],
          y: [0, -6, 0],
        }}
        transition={{
          opacity: { duration: 0.7, delay: 0.35 },
          scale: { duration: 0.7, delay: 0.35 },
          rotate: { duration: 11, repeat: Infinity, ease: 'easeInOut' },
          y: { duration: 7, repeat: Infinity, ease: 'easeInOut' },
        }}
        className="absolute right-[1%] top-[6%] w-[44%]"
      >
        <OroExpressCoin />
      </motion.div>

      {/* Lingote grande delante */}
      <motion.div
        initial={{ opacity: 0, y: 30, rotate: -8 }}
        animate={{
          opacity: 1,
          y: [0, -6, 0],
          rotate: [-8, -10, -8],
        }}
        transition={{
          opacity: { duration: 0.7, delay: 0.05 },
          y: { duration: 8, repeat: Infinity, ease: 'easeInOut' },
          rotate: { duration: 10, repeat: Infinity, ease: 'easeInOut' },
        }}
        className="absolute left-[10%] top-[28%] w-[78%] drop-shadow-[0_28px_56px_rgba(0,0,0,0.55)]"
      >
        <OroExpressIngot
          weight="100 g"
          serial="N.º OE-100-1024"
          tone="primary"
        />
      </motion.div>

      {/* Reflejo en el suelo */}
      <div
        aria-hidden
        className="absolute inset-x-[10%] bottom-[6%] h-6 rounded-full bg-gold-500/20 blur-2xl"
      />
    </div>
  );
}

function BullionDefs() {
  return (
    <svg
      width="0"
      height="0"
      viewBox="0 0 0 0"
      className="absolute"
      aria-hidden
    >
      <defs>
        {/* Fondo dorado mate-cepillado para el lingote */}
        <linearGradient id="ingot-base" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#b27d24" />
          <stop offset="22%" stopColor="#e8c882" />
          <stop offset="48%" stopColor="#f4d98c" />
          <stop offset="70%" stopColor="#d4a64a" />
          <stop offset="100%" stopColor="#7a4f10" />
        </linearGradient>
        <linearGradient id="ingot-base-dim" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8a601c" />
          <stop offset="20%" stopColor="#c5a463" />
          <stop offset="50%" stopColor="#d8b568" />
          <stop offset="75%" stopColor="#a07f30" />
          <stop offset="100%" stopColor="#5b3a08" />
        </linearGradient>
        {/* Bisel superior izquierdo (luz) */}
        <linearGradient id="ingot-light-bevel" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(255,255,255,0.55)" />
          <stop offset="40%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        {/* Bisel inferior derecho (sombra) */}
        <linearGradient id="ingot-dark-bevel" x1="0" y1="1" x2="0" y2="0">
          <stop offset="0%" stopColor="rgba(0,0,0,0.45)" />
          <stop offset="50%" stopColor="rgba(0,0,0,0)" />
        </linearGradient>
        {/* Reflejo de luz cruzado */}
        <linearGradient id="ingot-shine" x1="0" y1="0" x2="1" y2="0.6">
          <stop offset="0%" stopColor="rgba(255,255,255,0)" />
          <stop offset="38%" stopColor="rgba(255,255,255,0.45)" />
          <stop offset="55%" stopColor="rgba(255,255,255,0.15)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
        <radialGradient id="coin-base" cx="0.4" cy="0.3" r="0.85">
          <stop offset="0%" stopColor="#fde08e" />
          <stop offset="40%" stopColor="#e8c067" />
          <stop offset="75%" stopColor="#a87a26" />
          <stop offset="100%" stopColor="#5d3f0c" />
        </radialGradient>
        {/* Textura granular sutil para look mate */}
        <filter id="ingot-grain" x="0" y="0" width="100%" height="100%">
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.95"
            numOctaves="2"
            seed="3"
          />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0.6
                    0 0 0 0 0.45
                    0 0 0 0 0.15
                    0 0 0 0.18 0"
          />
          <feComposite in2="SourceGraphic" operator="in" />
        </filter>
      </defs>
    </svg>
  );
}

interface OroExpressIngotProps {
  weight: string;
  serial: string;
  tone?: 'primary' | 'dim';
}

function OroExpressIngot({
  weight,
  serial,
  tone = 'primary',
}: OroExpressIngotProps) {
  const fill = tone === 'primary' ? 'url(#ingot-base)' : 'url(#ingot-base-dim)';
  return (
    <svg viewBox="0 0 360 220" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      {/* sombra base */}
      <ellipse
        cx="180"
        cy="208"
        rx="150"
        ry="9"
        fill="#000"
        opacity="0.4"
      />

      {/* cuerpo del lingote con esquinas redondeadas */}
      <g>
        <rect x="14" y="14" width="332" height="190" rx="14" fill={fill} />
        <rect
          x="14"
          y="14"
          width="332"
          height="190"
          rx="14"
          filter="url(#ingot-grain)"
          opacity="0.45"
        />
        <rect
          x="14"
          y="14"
          width="332"
          height="190"
          rx="14"
          fill="url(#ingot-light-bevel)"
          opacity="0.7"
        />
        <rect
          x="14"
          y="14"
          width="332"
          height="190"
          rx="14"
          fill="url(#ingot-dark-bevel)"
          opacity="0.6"
        />
        <rect
          x="14"
          y="14"
          width="332"
          height="190"
          rx="14"
          fill="none"
          stroke="rgba(60,38,4,0.55)"
          strokeWidth="1.2"
        />
        <rect
          x="22"
          y="22"
          width="316"
          height="174"
          rx="10"
          fill="none"
          stroke="rgba(255,229,160,0.45)"
          strokeWidth="0.8"
        />
      </g>

      {/* Sello de pureza superior derecho */}
      <g transform="translate(282, 30)">
        <rect
          x="0"
          y="0"
          width="50"
          height="22"
          rx="3"
          fill="rgba(20,12,2,0.85)"
        />
        <text
          x="25"
          y="14"
          textAnchor="middle"
          fill="#f3d27a"
          fontFamily="Inter, sans-serif"
          fontSize="8"
          fontWeight="700"
          letterSpacing="2"
        >
          999,9
        </text>
      </g>

      {/* Marca Oro Express grande arriba-izquierda */}
      <g transform="translate(28, 30)">
        <text
          x="0"
          y="14"
          fill="#1a1208"
          fontFamily="Playfair Display, serif"
          fontSize="14"
          fontWeight="800"
          letterSpacing="3"
        >
          ORO EXPRESS
        </text>
        <text
          x="0"
          y="26"
          fill="rgba(26,18,8,0.7)"
          fontFamily="Inter, sans-serif"
          fontSize="6.5"
          letterSpacing="2"
        >
          INVESTMENT GOLD · MADRID
        </text>
      </g>

      {/* Texto principal: pureza */}
      <text
        x="180"
        y="108"
        textAnchor="middle"
        fill="#3a2807"
        fontFamily="Playfair Display, serif"
        fontSize="14"
        fontWeight="700"
        letterSpacing="5"
      >
        FINE GOLD
      </text>

      {/* Peso destacado */}
      <text
        x="180"
        y="158"
        textAnchor="middle"
        fill="#2c1d04"
        fontFamily="Playfair Display, serif"
        fontSize="34"
        fontWeight="800"
        letterSpacing="-1"
      >
        {weight}
      </text>

      {/* Reflejo cruzado (encima del contenido para realismo) */}
      <rect
        x="14"
        y="14"
        width="332"
        height="190"
        rx="14"
        fill="url(#ingot-shine)"
        opacity="0.5"
      />

      {/* Footer: serie + distribuidor */}
      <g>
        <text
          x="34"
          y="190"
          fill="rgba(58,40,7,0.85)"
          fontFamily="JetBrains Mono, monospace"
          fontSize="6.5"
          letterSpacing="1.5"
        >
          {serial}
        </text>
        <g transform="translate(228, 178)">
          <rect
            x="0"
            y="0"
            width="98"
            height="18"
            rx="3"
            fill="rgba(20,12,2,0.85)"
          />
          <text
            x="49"
            y="11"
            textAnchor="middle"
            fill="#f3d27a"
            fontFamily="Playfair Display, serif"
            fontSize="7"
            fontWeight="700"
            letterSpacing="2.5"
          >
            ORO EXPRESS
          </text>
          <text
            x="49"
            y="16"
            textAnchor="middle"
            fill="rgba(243,210,122,0.7)"
            fontFamily="Inter, sans-serif"
            fontSize="3.5"
            letterSpacing="1"
          >
            DISTRIBUIDOR OFICIAL
          </text>
        </g>
      </g>
    </svg>
  );
}

function OroExpressCoin() {
  return (
    <svg viewBox="0 0 220 220" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      {/* sombra base */}
      <ellipse
        cx="110"
        cy="206"
        rx="86"
        ry="6"
        fill="#000"
        opacity="0.45"
      />

      {/* moneda */}
      <g>
        <circle cx="110" cy="108" r="98" fill="url(#coin-base)" />
        <circle
          cx="110"
          cy="108"
          r="98"
          filter="url(#ingot-grain)"
          opacity="0.35"
        />
        <circle
          cx="110"
          cy="108"
          r="92"
          fill="none"
          stroke="rgba(60,38,4,0.6)"
          strokeWidth="1"
        />
        <circle
          cx="110"
          cy="108"
          r="80"
          fill="none"
          stroke="rgba(255,229,160,0.45)"
          strokeWidth="0.8"
        />
        {/* dentado del canto */}
        {Array.from({ length: 90 }).map((_, i) => {
          const a = (i / 90) * Math.PI * 2;
          const x1 = 110 + Math.cos(a) * 96;
          const y1 = 108 + Math.sin(a) * 96;
          const x2 = 110 + Math.cos(a) * 92;
          const y2 = 108 + Math.sin(a) * 92;
          return (
            <line
              key={i}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke="rgba(60,38,4,0.5)"
              strokeWidth="0.7"
            />
          );
        })}

        {/* texto envolvente superior */}
        <path
          id="coin-arc-top"
          d="M 30 108 A 80 80 0 0 1 190 108"
          fill="none"
        />
        <text
          fill="#3a2807"
          fontFamily="Playfair Display, serif"
          fontSize="11"
          fontWeight="700"
          letterSpacing="2.5"
        >
          <textPath href="#coin-arc-top" startOffset="50%" textAnchor="middle">
            ORO EXPRESS · MADRID
          </textPath>
        </text>

        {/* monograma OE central */}
        <g transform="translate(110,118)">
          <circle
            r="26"
            fill="rgba(58,40,7,0.18)"
            stroke="rgba(58,40,7,0.85)"
            strokeWidth="1"
          />
          <text
            textAnchor="middle"
            y="6"
            fill="#2c1d04"
            fontFamily="Playfair Display, serif"
            fontSize="22"
            fontWeight="900"
            letterSpacing="-1"
          >
            OE
          </text>
        </g>

        {/* peso y pureza */}
        <text
          x="110"
          y="160"
          textAnchor="middle"
          fill="#2c1d04"
          fontFamily="Playfair Display, serif"
          fontSize="14"
          fontWeight="800"
          letterSpacing="1"
        >
          1 OZ
        </text>
        <text
          x="110"
          y="176"
          textAnchor="middle"
          fill="rgba(58,40,7,0.85)"
          fontFamily="JetBrains Mono, monospace"
          fontSize="6.5"
          letterSpacing="2"
        >
          999,9 FINE GOLD
        </text>

        {/* brillo radial */}
        <ellipse
          cx="80"
          cy="70"
          rx="60"
          ry="32"
          fill="rgba(255,255,255,0.35)"
        />
      </g>
    </svg>
  );
}
